import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSEO } from '@/hooks/useSEO';

const GoogleCalendarOAuthCallback = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your Google Calendar connection...');

  useSEO({
    title: "Google Calendar - OAuth Callback",
    description: "Completing your Google Calendar integration",
    canonical: "/oauth/google-calendar/callback"
  });

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        console.log('📝 OAuth callback received:', { 
          code: code ? code.substring(0, 10) + '...' : null, 
          state, 
          error, 
          errorDescription 
        });

        if (error) {
          console.error('❌ OAuth error:', error, errorDescription);
          setStatus('error');
          setMessage(`Authentication failed: ${errorDescription || error}`);
          toast.error(`Google Calendar connection failed: ${errorDescription || error}`);
          return;
        }

        if (!code || !state) {
          console.error('❌ Missing code or state parameter');
          setStatus('error');
          setMessage('Invalid callback parameters');
          toast.error('Invalid authentication response');
          return;
        }

        // Verify state parameter - Note: For production cross-domain flows,
        // we'll let the edge function handle state validation if needed
        const storedState = sessionStorage.getItem('gc_oauth_state');
        console.log('🔍 State verification:', { received: state, stored: storedState });
        
        // Only validate state if we're on the same domain as where it was set
        const currentDomain = window.location.hostname;
        console.log('🌐 Current domain:', currentDomain);
        
        if (storedState && state !== storedState) {
          console.error('❌ State mismatch:', { received: state, stored: storedState });
          setStatus('error');
          setMessage('Security validation failed');
          toast.error('Authentication security check failed');
          return;
        }

        console.log('🔄 Exchanging code for tokens...');

        // Exchange code for tokens through edge function
        const { data, error: exchangeError } = await supabase.functions.invoke('google-calendar-auth', {
          body: { 
            action: 'exchange_code', 
            code: code,
            state: state
          }
        });

        if (exchangeError) {
          console.error('❌ Token exchange failed:', exchangeError);
          setStatus('error');
          setMessage('Failed to complete authentication');
          toast.error('Failed to connect Google Calendar');
          return;
        }

        console.log('✅ Google Calendar connected successfully');
        setStatus('success');
        setMessage('Google Calendar connected successfully!');
        toast.success('Google Calendar connected successfully!');

        // Clean up
        sessionStorage.removeItem('gc_oauth_state');
        
        // Get return URL from session storage or default to settings
        const returnUrl = sessionStorage.getItem('gc_return_url') || '/practitioner-dashboard?tab=settings';
        sessionStorage.removeItem('gc_return_url');

        // Redirect back to the return URL after a brief delay
        setTimeout(() => {
          window.location.href = returnUrl;
        }, 2000);

      } catch (error) {
        console.error('❌ Unexpected error in callback:', error);
        setStatus('error');
        setMessage('An unexpected error occurred');
        toast.error('An unexpected error occurred');
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center p-4">
        <article className="text-center space-y-4 max-w-md mx-auto">
          <h1 className="text-2xl font-bold">Google Calendar Integration</h1>
          
          <div className="flex items-center justify-center space-x-2">
            {status === 'processing' && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
            {status === 'success' && <CheckCircle className="h-6 w-6 text-green-500" />}
            {status === 'error' && <XCircle className="h-6 w-6 text-red-500" />}
            <span className="text-lg">{message}</span>
          </div>

          {status === 'success' && (
            <p className="text-muted-foreground">
              Redirecting you back to settings...
            </p>
          )}

          {status === 'error' && (
            <div className="space-y-2">
              <p className="text-muted-foreground">
                Please try connecting again or contact support if the issue persists.
              </p>
              <Button 
                onClick={() => window.location.href = '/practitioner-dashboard?tab=settings'}
                variant="outline"
              >
                Back to Dashboard
              </Button>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default GoogleCalendarOAuthCallback;